import '../overrides/slate';
import Debug from 'debug';
import { cloneDeep, isEqual } from 'lodash';
import {
  Editor,
  PointRef,
  RangeRef,
  PathRef,
  Path,
  Text,
  Transforms,
  Operation,
} from 'slate';

import {
  applyOpToValue,
  changeRange,
  textOpBounds,
  fixupOperations,
  fixupCursorPosition,
  makeAnnotationRangeRefs,
  operationHistory,
  snapshotEditor,
} from './utils';
import { transformCursor } from './transformCursor';
import { validateComponent } from './validateComponent';
import { transform } from './transform';

import './transforms/insert_text';
import './transforms/remove_text';
import './transforms/insert_node';
import './transforms/merge_node';
import './transforms/move_node';
import './transforms/remove_node';
import './transforms/split_node';
import './transforms/set_node';
import './transforms/add_annotation';
import './transforms/set_annotation';
import './transforms/remove_annotation';
import './transforms/add_metadata';
import './transforms/remove_metadata';
import './transforms/set_metadata';
import './transforms/set_selection';

// Using the bootstrapTransform function from the json0 ottype. It's
// not the best way to handle multiple components in an operation, but
// I haven't found any good research for writing sparse document
// traversal with tree-like documents, and I need to learn more about
// how this all works before taking a stab at it.
import bootstrapTransform from './bootstrapTransform';
import OperationTransforms from './transforms/index';

const debug = Debug('ot-slate:type');

const applyAsync = (snapshot, ops, callback) => {
  const op = ops.shift();
  if (
    (typeof window !== 'undefined' && window.setImmediate) ||
    (typeof global !== 'undefined' && global.setImmediate)
  ) {
    setImmediate(applyAsync, applyOpToValue(snapshot, op), ops, callback);
  } else {
    setTimeout(
      () => applyAsync(applyOpToValue(snapshot, op), ops, callback),
      0
    );
  }
};

function previousPath(editor, path) {
  const previousNode = Editor.previous(editor, {
    at: path,
  });
  return previousNode ? previousNode[1] : null;
}

function nextPath(editor, path) {
  const nextNode = Editor.next(editor, {
    at: path,
  });

  return nextNode ? nextNode[1] : null;
}

function fixupPath(editor, path, op, ref) {
  switch (op.type) {
    case 'remove_node': {
      // ref will be nullified if point is deleted. Instead,
      // preemptively move it to the previous text node if possible,
      // and the next text node otherwise.
      if (Path.equals(op.path, path) || Path.isAncestor(op.path, path)) {
        try {
          if (ref.affinity === 'forward') {
            return (
              nextPath(editor, op.path) ?? previousPath(editor, op.path) ?? path
            );
          } else {
            return (
              previousPath(editor, op.path) ?? nextPath(editor, op.path) ?? path
            );
          }
        } catch (e) {
          // If normalizations haven't run yet, path may be referring
          // to a path without text nodes. This breaks an assumption
          // of Editor.next/previous, and there doesn't seem to be a
          // way around it. So we will just ignore those errors -- we
          // may end up with invalid refs still, but we don't have a
          // good way to fix them, so it's the best we can do. We
          // don't currently use pathRefs anyway, and Slate doesn't
          // seem to mind them going invalid once in a while.
          if (
            !e.toString().match(/Cannot get the.*because it has no.*text node/)
          ) {
            throw e;
          }
        }
      }
      break;
    }
    case 'split_node': {
      if (ref.affinity === null) {
        // Null makes no sense and is only used to break refs
        // when split node happens at the same point. Make it
        // "forward" instead, which is the default.
        ref.affinity = 'forward';
      }
      break;
    }
  }
  return path;
}

function previousPoint(editor, path) {
  try {
    const previousTextNode = Editor.previous(editor, {
      at: path,
      match: Text.isText,
    });
    return previousTextNode
      ? {
          path: previousTextNode[1],
          offset: previousTextNode[0].text.length,
        }
      : null;
  } catch {
    return null;
  }
}

function nextPoint(editor, path) {
  try {
    const nextTextNode = Editor.next(editor, {
      at: path,
      match: Text.isText,
    });

    return nextTextNode
      ? {
          path: nextTextNode[1],
          offset: 0,
        }
      : null;
  } catch {
    return null;
  }
}

function fixupPoint(editor, point, op, ref) {
  switch (op.type) {
    case 'remove_node': {
      // ref will be nullified if point is deleted. Instead,
      // preemptively move it to the nearest text node if possible.
      if (
        Path.equals(op.path, point.path) ||
        Path.isAncestor(op.path, point.path)
      ) {
        if (ref.affinity === 'forward') {
          return (
            nextPoint(editor, op.path) ??
            previousPoint(editor, op.path) ??
            point
          );
        } else {
          return (
            previousPoint(editor, op.path) ??
            nextPoint(editor, op.path) ??
            point
          );
        }
      }
      break;
    }
    case 'split_node': {
      if (ref.affinity === null) {
        // Null makes no sense and is only used to break transforms when
        // split node happens at the same point. Make it "forward"
        // instead, which is the default.
        ref.affinity = 'forward';
      }
      break;
    }
  }

  return point;
}

// Slate's Point/Path transform functions mostly work, but there are
// some edge cases where it returns null. For example, if you delete a
// node containing an endpoint of a range, the range becomes null.
// This is incorrect for the way we use ranges -- we would like the
// point to move to the nearest valid point. I can see why Slate does
// this, because its transform functions don't have access to the
// editor content to find a valid point. But it's still broken.
//
// There isn't a good way to intercept Slate's handling of these
// cases. Instead, we can pre-emptively move the point or path to a
// valid position that will be transformed into the right place when
// the operation runs. For example, we can move a range endpoint into
// the previous text node that is not part of the delete.
//
// fixupRefs will do this for all active refs in the editor, and
// should always run before Slate's transform functions run.
function fixupRefs(editor, op) {
  for (const ref of Editor.pathRefs(editor)) {
    ref.current = fixupPath(editor, ref.current, op, ref);
  }

  for (const ref of Editor.pointRefs(editor)) {
    ref.current = fixupPoint(editor, ref.current, op, ref);
  }

  for (const ref of Editor.rangeRefs(editor)) {
    const anchor = fixupPoint(editor, ref.current.anchor, op, ref);
    const focus = fixupPoint(editor, ref.current.focus, op, ref);
    ref.current = {
      ...ref.current,
      anchor,
      focus,
    };
  }
}

function applyCollaborativeOperation(editor, op) {
  fixupRefs(editor, op);

  for (const ref of Editor.pathRefs(editor)) {
    PathRef.transform(ref, op);
  }

  for (const ref of Editor.pointRefs(editor)) {
    PointRef.transform(ref, op);
  }

  for (const ref of Editor.rangeRefs(editor)) {
    RangeRef.transform(ref, op);
  }

  Transforms.transform(editor, op);
}

let lastSnapshot;
const apply = (snapshot, ops) => {
  if (ops.some((op) => op.object === 'operation')) {
    throw new Error('Editor v1 operation received on v2 document, aborting.');
  }

  if (debug.enabled) {
    debug(`applying ${JSON.stringify(ops)}\n  to ${JSON.stringify(snapshot)}`);
  }

  if (ops.length > 0) {
    const lastOp = ops[ops.length - 1];
    if (lastOp.value) {
      // We've already calculated all the intermediate values -- we just
      // need the last one.

      if (debug.enabled) {
        debug(`  using cached ${JSON.stringify(lastOp.value)}`);
      }

      const newValue = applyOpToValue(lastOp.value, lastOp);

      if (debug.enabled) {
        debug(`  became ${JSON.stringify(newValue)}`);
      }

      return newValue;
    }
  }

  const editor = snapshotEditor(snapshot);

  Editor.withoutNormalizing(editor, () => {
    for (const op of ops) {
      applyCollaborativeOperation(editor, op);
    }
  });

  lastSnapshot = snapshotEditor(editor);

  if (debug.enabled) debug(`  became ${JSON.stringify(lastSnapshot)}`);

  return lastSnapshot;
};

const create = (initialData) => {
  if (!initialData) {
    initialData = snapshotEditor();
  }
  console.log('Creating Document with data', JSON.stringify(initialData));
  return initialData;
};

const composeComponent = (lastOp, op) => {
  // Since the composed operation starts with lastOp, this always
  // needs to either return lastOp, or set the value of the returned
  // op to lastOp's value.
  if (lastOp.type === 'insert_text') {
    if (
      op.type === 'insert_text' &&
      Path.equals(lastOp.path, op.path) &&
      lastOp.offset + lastOp.text.length === op.offset
    ) {
      return {
        ...lastOp,
        text: lastOp.text + op.text,
      };
    }
    if (op.type === 'remove_text' && Path.equals(lastOp.path, op.path)) {
      const insertBounds = textOpBounds(lastOp);
      const removeBounds = textOpBounds(op);
      if (
        removeBounds.start >= insertBounds.start &&
        removeBounds.end <= insertBounds.end
      ) {
        if (
          op.text ===
          lastOp.text.slice(
            removeBounds.start - insertBounds.start,
            removeBounds.end - insertBounds.start + 1
          )
        ) {
          return {
            ...lastOp,
            text: lastOp.text
              .slice(0, removeBounds.start)
              .concat(lastOp.text.slice(removeBounds.end + 1, -1)),
          };
        }
      }
    }
  }

  if (lastOp.type === 'remove_text') {
    if (op.type === 'remove_text') {
      if (Path.equals(lastOp.path, op.path)) {
        if (op.offset + op.text.length === lastOp.offset) {
          // backward delete
          return {
            ...lastOp,
            text: op.text + lastOp.text,
            offset: op.offset,
          };
        } else if (lastOp.offset + lastOp.text.length === op.offset) {
          // forward delete
          return {
            ...lastOp,
            text: lastOp.text + op.text,
          };
        }
      }
    }
  }
  return lastOp;
};

const compose = (op1, op2) => {
  return op1.concat(op2).reduce((acc, op) => {
    if (acc.length > 0) {
      const lastOp = acc[acc.length - 1];

      const composedOp = composeComponent(lastOp, op);

      if (!isEqual(composedOp, lastOp)) {
        if (debug.enabled) {
          validateComponent(lastSnapshot, composedOp);
        }
        return acc.slice(0, -1).concat(composedOp);
      }
    }
    return acc.concat([op]);
  }, []);
};

const normalizeDocument = (snapshot, ops) => {
  const lastOp = ops[ops.length - 1];
  let normalizingOps = [];

  const editor = snapshotEditor(snapshot);
  Editor.withoutNormalizing(editor, () => {
    applyOpToValue(editor, lastOp);
    editor.normalizeNode([editor, []], { operation: lastOp });

    normalizingOps = editor.operations.filter(
      (op) => op.type !== 'set_selection' && op.type !== 'set_value'
    );
  });

  if (debug.enabled) {
    debug(`normalizing with ${JSON.stringify(normalizingOps)}`);
  }

  return normalizingOps;
};

const transformComponent = (dest, op1, op2, side) => {
  const transformedOps = transform(op1, op2, side);
  append(dest, transformedOps);
  return dest;
};

const append = (dest, op) => {
  if (!op) return;
  Array.isArray(op) ? op.forEach((e) => dest.push(e)) : dest.push(op);
};

const transformSelection = (selection, ops, value = null) => {
  const data = {};

  return ops.reduce(
    (selection, op) =>
      changeRange(selection, data, (cursor, cursorDetails) => {
        let newCursor = transformCursor(cursor, cursorDetails, op, false);
        if (value) {
          newCursor = fixupCursorPosition(newCursor, cursorDetails, value);
        }
        return newCursor;
      }),
    selection
  );
};

const type = {
  name: 'slate-v2',
  uri: 'https://github.com/aha-app/ot-slate',
  apply,
  applyAsync,
  compose,
  composeComponent,
  create,
  normalizeDocument,
  transformCursor,
  transformSelection,
};

const transformer = {};

bootstrapTransform(transformer, transformComponent, append);

type.transform = transformer.transform;

// Override the transformX method to support Slate
// normalization. After transforming the two sets of operations, we
// run normalize on the resulting values, and append any ops needed to
// normalize the doc to each side. These should be identical!  For
// example, if you have a two-node document, and Client 0 runs
// {remove_node, 0} and Client 1 runs {remove_node, 1}, Transformed 0
// becomes [{remove_node, 0}, {insert_node, 0}] and Transformed 1
// becomes [{remove_node, 0}, {insert_node, 0}]. This brings both
// sides into a consistent state, and normalizes the document.  NOTE:
// This means that all clients, including the ShareDB servers, must
// normalize documents in *exactly* the same way.
type.transformX = (leftOp, rightOp, intermediateValues = {}) => {
  const { editor } = intermediateValues;
  const startValue = editor && cloneDeep(editor.children);

  let left;
  let right;

  try {
    [left, right] = transformer.transformX(leftOp, rightOp);
  } catch (e) {
    console.error('Error during transformation');
    if (debug.enabled) {
      debug(operationHistory(startValue, leftOp, rightOp));
    }
    throw e;
  }

  // If we didn't have a value to start with, there's nothing to
  // normalize.
  if (!startValue) {
    return [left, right];
  }

  let normalizingOps;

  try {
    // If we were passed a value, fix up all of the operations so they'd
    // correctly apply to the resulting value after all of the
    // non-transformed operations succeed. This is necessary to support
    // undo / redo of remove_node and set_selection operations.
    const leftValue = apply(snapshotEditor(editor), leftOp);
    intermediateValues.leftValue = leftValue;
    right = fixupOperations(leftValue, right);

    const rightValue = apply(snapshotEditor(editor), rightOp);
    intermediateValues.rightValue = rightValue;
    left = fixupOperations(rightValue, left);

    // At this point, we're guaranteed that
    //
    // apply(lastValue(leftOp + right), right[right.length - 1]) ===
    // apply(lastValue(rightOp + left), left[left.length - 1])
    //
    // Otherwise, there had to have been a bug in transformation.  So,
    // we can do any normalization we need on the left value, and then
    // just use the same normalizing operations on both lists.

    // Fast-forward to the second-to-last value.
    const allOps = rightOp.concat(left);

    const lastValue = apply(snapshotEditor(editor), allOps.slice(0, -1));
    editor.children = cloneDeep(lastValue.children);

    normalizingOps = normalizeDocument(editor, allOps);

    const normalizedLeft = left.concat(normalizingOps);
    const normalizedRight = right.concat(normalizingOps);

    intermediateValues.endValue = apply(
      snapshotEditor(editor),
      allOps.slice(-1).concat(normalizingOps)
    );

    return [normalizedLeft, normalizedRight];
  } catch (e) {
    console.error('Error generating normalizing operations');
    if (debug.enabled) {
      debug(operationHistory(startValue, leftOp, rightOp, left, right));
    }
    throw e;
  }
};

export {
  type,
  Transforms,
  Operation,
  OperationTransforms,
  fixupRefs,
  applyCollaborativeOperation,
  makeAnnotationRangeRefs,
  snapshotEditor,
};
